import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FiCopy } from 'react-icons/fi';
import {
    btnGhost,
    btnPrimary,
    inputClass,
    checkboxClass,
    copyText,
    buildCopyText,
    defaultFields,
    FIELD_OPTIONS
} from './ui';

const SEPARATORS = [
    { value: ',', label: 'Comma' },
    { value: ' ', label: 'Space' },
    { value: '\n', label: 'New line' }
];

const LAYOUTS = [
    { value: 'lines', label: 'One order per line' },
    { value: 'single', label: 'Single line' }
];

/**
 * Turns the flagged orders into text for the provider. Which columns go in is
 * the operator's call — usually just the provider's external IDs, sometimes the
 * full detail for a ticket.
 */
export default function CopyToolbar({ flagged, selected }) {
    const [fields, setFields] = useState(defaultFields);
    const [layout, setLayout] = useState('lines');
    const [separator, setSeparator] = useState(',');

    const anyField = Object.values(fields).some(Boolean);
    const selectedText = useMemo(
        () => (anyField ? buildCopyText(selected, fields, layout, separator) : ''),
        [selected, fields, layout, separator, anyField]
    );
    const allText = useMemo(
        () => (anyField ? buildCopyText(flagged, fields, layout, separator) : ''),
        [flagged, fields, layout, separator, anyField]
    );

    const copy = async (text, count) => {
        if (!count) return toast.error('Nothing to copy');
        if (!anyField) return toast.error('Pick at least one thing to copy');
        const ok = await copyText(text);
        if (ok) toast.success(`Copied ${count} order${count === 1 ? '' : 's'}`);
        else toast.error('Copy failed — select the preview text and copy manually');
    };

    return (
        <div className="card p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-sm font-semibold text-foreground">Copy for the provider</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Choose what goes into the message, then copy the selected rows or the whole flagged list.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={() => copy(selectedText, selected.length)}
                        className={btnPrimary}
                        disabled={!selected.length}
                    >
                        <FiCopy size={14} /> Copy selected ({selected.length})
                    </button>
                    <button
                        type="button"
                        onClick={() => copy(allText, flagged.length)}
                        className={btnGhost}
                        disabled={!flagged.length}
                    >
                        <FiCopy size={14} /> Copy all flagged ({flagged.length})
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Include</span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {FIELD_OPTIONS.map(option => (
                            <label key={option.key} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                <input
                                    type="checkbox"
                                    className={checkboxClass}
                                    checked={!!fields[option.key]}
                                    onChange={e => setFields(prev => ({ ...prev, [option.key]: e.target.checked }))}
                                />
                                {option.label}
                            </label>
                        ))}
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-1">
                        <div className="space-y-1">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Layout</span>
                            <select
                                className={inputClass}
                                value={layout}
                                onChange={e => setLayout(e.target.value)}
                            >
                                {LAYOUTS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Separator</span>
                            <select
                                className={inputClass}
                                value={separator}
                                onChange={e => setSeparator(e.target.value)}
                                disabled={layout !== 'single'}
                            >
                                {SEPARATORS.map(option => (
                                    <option key={option.label} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="space-y-2 min-w-0">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Preview</span>
                    <pre className="text-xs font-mono bg-muted/40 border border-border rounded-md p-3 h-40 overflow-auto whitespace-pre-wrap break-all text-foreground">
                        {selectedText || allText || 'Nothing selected yet.'}
                    </pre>
                </div>
            </div>
        </div>
    );
}
